import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { s3baseUri } from "../../config/config";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import CustomMUITable from "../../components/customtable/CustomMuiTable";
import PageLoading from "../../components/loader/PageLoading";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import { useIds } from "../../hook/AdminContext";
import {
  addCommasToNumber,
  formatSalary,
  getFileIcon,
  show_proper_words,
} from "../../utils/Constant";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  PictureAsPdfOutlined,
  DescriptionOutlined,
  Search,
} from "@mui/icons-material";
import { applied_hob_list_for_candidate_client } from "../../DAL/jobs/jobs";

const AppliedJobs = () => {
  const [membersList, setMembersList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openDetailPopup, setOpenDetailPopup] = useState(false);
  const [cvDownloadLoading, setCvDownloadLoading] = useState(false);
  const [additionalDocDownloadLoading, setAdditionalDocDownloadLoading] =
    useState({});

  const [searchText, setSearchText] = useState("");

  const [selectedMember, setSelectedMember] = useState(null);
  const [pageCount, setPageCount] = useState(1);
  const [forcedWidth, setForcedWidth] = useState(0);

  const { findId, setBreadCrumb, userInfo } = useIds();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const sourceSlug = params?.source_slug || "";

  const homePath = location?.state?.routePath;

  const handleChangeRowsPerPage = (event) => {
    setIsLoading(true);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(0);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(0);
    } else {
      setPageCount(newPage + 1);
    }
  };

  const handleDownloadClick = async () => {
    if (!selectedMember?.cv) {
      enqueueSnackbar("No CV available to download", { variant: "error" });
      return;
    }

    const fileUrl = `${s3baseUri.replace(
      /\/$/,
      ""
    )}/${selectedMember.cv.replace(/^\//, "")}`;

    try {
      setCvDownloadLoading(true);

      const response = await fetch(fileUrl, {
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application")) {
        throw new Error("Invalid file content");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const fileExtension = selectedMember.cv.split(".").pop();
      const cvName = selectedMember.cv.split("/").pop();

      const firstName =
        selectedMember?.first_name?.trim() ||
        userInfo?.first_name?.trim() ||
        userInfo?.company_name?.trim();

      const lastName =
        selectedMember?.last_name?.trim() || userInfo?.last_name?.trim() || "";

      const fullName =
        `${firstName} ${lastName}`.trim().replace(/\s+/g, "_") ||
        "unknown_user";

      const fileName = `CV_${fullName}.${fileExtension}`;

      const link = document.createElement("a");
      link.href = url;
      link.download = cvName || fileName;

      document.body.appendChild(link);

      // Safari Fix
      if (
        navigator.userAgent.includes("Safari") &&
        !navigator.userAgent.includes("Chrome")
      ) {
        window.open(url, "_blank");
      } else {
        link.click();
      }

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      enqueueSnackbar("Error downloading the file", { variant: "error" });
      window.open(fileUrl, "_blank");
    } finally {
      setCvDownloadLoading(false);
    }
  };

  const handleDownloadAdditiondOC = async (documentPath) => {
    const fileUrl = `${s3baseUri}${documentPath}`;
    const filename = documentPath.split("/").pop();

    try {
      setAdditionalDocDownloadLoading((prev) => ({
        ...prev,
        [documentPath]: true,
      }));

      const response = await fetch(fileUrl, { mode: "cors" });
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setAdditionalDocDownloadLoading((prev) => ({
        ...prev,
        [documentPath]: false,
      }));
    }
  };

  const TABLE_HEAD = [
    { id: "action", label: "Action", type: "action" },
    { id: "number", label: "#", type: "number" },
    // { id: "name_profile", label: "Image ", type: "title_with_img" },
    {
      id: "job_title",
      label: "Job Title",
      renderData: (row) => {
        return row.job.job_title;
      },
    },
    {
      id: "company_name",
      label: "Company",
      renderData: (row) => {
        return row.job.created_by?.company_name;
      },
    },
    {
      id: "location",
      label: "Location",
      renderData: (row) => {
        return row.job.location;
      },
    },
    {
      id: "job_type.title",
      label: "Job Type",
      renderData: (row) => {
        return row.job.job_type?.title;
      },
    },

    // {
    //   id: "email",
    //   label: "Email",
    //   type: "email",
    //   renderData: (row) => {
    //     const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${row.email}`;

    //     return (
    //       <a
    //         href={gmailLink}
    //         target="_blank"
    //         rel="noopener noreferrer"
    //         style={{
    //           cursor: "pointer",
    //           color: "blue",
    //           textDecoration: "underline",
    //         }}
    //       >
    //         {row.email}
    //       </a>
    //     );
    //   },
    // },
    { id: "date_of_birth", label: "Applied  At" },
  ];

  const firstSegment = location.pathname.split("/")[1];

  const fetchData = async () => {
    const result = await applied_hob_list_for_candidate_client(
      searchText,
      sourceSlug,
      page,
      rowsPerPage
    );

    if (result.code === 200) {
      const members = result.jobs.map((job, index) => {
        const jobData = job.jb || {};
        const createdBy = jobData.created_by || {};

        return {
          ...job,
          ...jobData, // Flatten Job_id fields into top-level object
          name_profile: {
            image: s3baseUri + createdBy.profile_image?.thumbnail_1,
          },
          //   firs_name: createdBy.first_name || createdBy.company_name || "N/A",
          //   last_name: createdBy.last_name || "N/A",
          date_of_birth: moment(job.createdAt).format("DD/MM/YYYY hh:mm A"),
          biography: jobData.biography,
          email: createdBy.user_id?.email || "N/A",
        };
      });

      setTotalCount(result?.total_count);
      setTotalPages(Math.ceil(result?.total_pages - 1) || 0);
      setMembersList(members);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };
  // const handleChangePages = (event, newPage) => {
  //   if (newPage <= 0) {
  //     setPage(0);
  //   } else {
  //     setPage(newPage - 1);
  //   }
  // };

  useEffect(() => {
    const crumbs = [
      {
        name: `${show_proper_words(firstSegment)}`,
        path: `/${firstSegment}`,
        active: false,
      },
    ];

    if (homePath !== "home") {
      crumbs.push({
        name: `Live Job Feed`,
        path: `/${firstSegment}/live-job-feed`,
        active: false,
      });
    }

    crumbs.push({
      name: `Applied Jobs`,
      path: ``,
      active: true,
    });

    setBreadCrumb(crumbs);
  }, []);
  const searchFunction = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setPage(0);
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const custom_width = document.querySelector(".right-section").offsetWidth;
    if (isLoading && forcedWidth === 0) {
      setForcedWidth(custom_width);
    }
  }, [isLoading]);

  const handleDetailPopUp = (member) => {
    setSelectedMember(member);
    setOpenDetailPopup(true);
  };

  const handleCloseDetailPopup = () => {
    setOpenDetailPopup(false);
    setSelectedMember(null);
  };
  const MENU_OPTIONS = [
    {
      label: "View Details",
      icon: "bxs:detail",
      handleClick: handleDetailPopUp,
    },
  ];

  const formatSalary = (job) => {
    const frequency = job.frequency_salary?.toLowerCase() || "";
    if (job.salary_type === "fixed") {
      return job.salary
        ? `€${addCommasToNumber(job.salary)} / ${frequency}`
        : "N/A";
    }

    if (job.min_salary && job.max_salary) {
      return `€${addCommasToNumber(job.min_salary)} - €${addCommasToNumber(
        job.max_salary
      )} / ${frequency}`;
    }

    return "N/A";
  };

  return (
    <div className="container-fluid" style={{ maxWidth: forcedWidth }}>
      {/* giving force width to this screen due to collapsing issue of table */}
      <div className="row">
        <div className="col-12 my-2 main-heading-live-jobs">
          <h4>
            Applied Jobs{" "}
            <span style={{ fontWeight: "normal", fontSize: "16px" }}>
              (
              {userInfo?.company_name
                ? userInfo.company_name
                : `${userInfo?.first_name} ${userInfo?.last_name}`.trim()}
              )
            </span>
          </h4>
        </div>
        <div className="col-12">
          {isLoading ? (
            <PageLoading />
          ) : (
            <CustomMUITable
              TABLE_HEAD={TABLE_HEAD}
              data={membersList}
              pagePagination={true}
              MENU_OPTIONS={MENU_OPTIONS}
              custom_pagination={{
                total_count: totalCount,
                rows_per_page: rowsPerPage,
                page: page,
                handleChangePage: handleChangePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
              }}
              custom_search={{
                searchText: searchText,
                setSearchText: setSearchText,
                handleSubmit: searchFunction,
              }}
              pageCount={pageCount}
              totalPages={totalPages}
              handleChangePages={handleChangePages}
            />
          )}
        </div>
      </div>

      {/* Detail Popup */}
      <Dialog
        open={openDetailPopup}
        onClose={handleCloseDetailPopup}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle variant="h6" sx={{ fontWeight: "bold", fontSize: "24px" }}>
          Applied Job Details
          <span style={{ fontWeight: "normal", fontSize: "19px" }}>
            {" "}
            ({selectedMember?.job?.job_title || "N/A"})
          </span>
        </DialogTitle>

        <DialogContent dividers>
          {selectedMember && (
            <Grid container spacing={3}>
              {/* Application Summary */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  Application Summary
                </Typography>

                <Grid container spacing={2}>
                  {/* CV */}

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">
                      <strong style={{ fontWeight: "bold" }}>Applied At</strong>
                    </Typography>
                    <Typography>{selectedMember.date_of_birth}</Typography>
                  </Grid>

                  {selectedMember.cv && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        CV
                      </Typography>
                      <Tooltip title={cvDownloadLoading ? "" : "View CV"}>
                        <IconButton
                          onClick={handleDownloadClick}
                          disabled={cvDownloadLoading}
                          sx={{ color: "navy" }}
                        >
                          {cvDownloadLoading ? (
                            <CircularProgress
                              size={20}
                              sx={{ color: "navy" }}
                            />
                          ) : (
                            getFileIcon(selectedMember.cv, 35)
                          )}
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}

                  {/* Additional Documents */}
                  {selectedMember.additional_documents?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        Additional Documents
                      </Typography>
                      <Grid container spacing={1}>
                        {selectedMember.additional_documents.map(
                          (doc, index) => (
                            <Grid item key={index}>
                              <Tooltip
                                title={
                                  additionalDocDownloadLoading[doc]
                                    ? ""
                                    : doc.split("/").pop()
                                }
                              >
                                <IconButton
                                  onClick={() => handleDownloadAdditiondOC(doc)}
                                  disabled={additionalDocDownloadLoading[doc]}
                                  sx={{ color: "navy" }}
                                >
                                  {additionalDocDownloadLoading[doc] ? (
                                    <CircularProgress
                                      size={20}
                                      sx={{ color: "navy" }}
                                    />
                                  ) : (
                                    getFileIcon(doc, 35)
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          )
                        )}
                      </Grid>
                    </Grid>
                  )}

                  {/* Cover Letter */}
                  {selectedMember.job_description && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        Cover Letter
                      </Typography>
                      <Typography
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {selectedMember.job_description}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Job Details */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  Job Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Job Title
                    </Typography>
                    <Typography>
                      {selectedMember.job.job_title || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Location
                    </Typography>
                    <Typography>
                      {selectedMember.job.location || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Job Type
                    </Typography>
                    <Typography>
                      {selectedMember.job.job_type?.title || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Salary
                    </Typography>
                    <Typography>{formatSalary(selectedMember.job)}</Typography>
                  </Grid>

                  {/* Job Description */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Job Description
                    </Typography>
                    <Typography
                      component="div"
                      dangerouslySetInnerHTML={{
                        __html: selectedMember.job?.description || "N/A",
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <button
            className="small-button-contain-job-candidates"
            onClick={handleCloseDetailPopup}
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AppliedJobs;
